import { useEffect, useState } from 'react';
import braintree from 'braintree-web';
import {
  useStripe,
  useElements,
  CardNumberElement,
} from '@stripe/react-stripe-js';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  Typography,
  Radio,
  Stack,
  Alert,
  CircularProgress,
  FormControlLabel,
  Breadcrumbs,
  Link as MuiLink,
} from '@mui/material';
import {
  selectIsLoggedIn,
  selectUserRole,
  selectUser,
} from 'renderer/features/auth/state/userSelectors';
import { useAppSelector } from 'renderer/shared/state/hooks';
import AccountForm from 'renderer/features/settings/components/AccountForm';
import BillingAddressForm from 'renderer/features/settings/components/BillingAddressForm';
import { useGetProductDetailsQuery } from 'renderer/features/products-page/services/products.endpoints';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import { Form, Formik } from 'formik';
import { ZodSchema } from 'zod';
import { toValidator } from 'renderer/shared/schemas/common.schemas';
import OrderSummary from './OrderSummary';
import DiscountCode from './DiscountCode';
import MainCard from './MainCard';

import {
  LoggedInUserExistingCardSchema,
  LoggedInUserNewCardSchema,
  PublicUserNewAccountSchema,
  PublicUserNoAccountSchema,
  PurchaseForm,
} from '../schemas/purchase.schemas';
import {
  useGetConfigQuery,
  useMakePurchaseMutation,
} from '../services/purchase.endpoints';
import PurchaseConfirmationPage from './PurchaseConfirmationPage';
import StripeCreditCardForm from './StripeCreditCardForm';
import ExistingPaymentMethods from './ExistingPaymentMethods';
import PaymentMethodForm from './PaymentMethodForm';
// import "./styles.css";

const FullScreenLoader = ({ message }: { message: string }) => {
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        zIndex: 1000,
      }}
    >
      <CircularProgress />
      {message && (
        <Typography variant="h6" sx={{ mt: 2 }}>
          {message}
        </Typography>
      )}
    </Box>
  );
};

const initialForm: PurchaseForm = {
  nameOnCard: '',
  email: '',
  firstName: '',
  lastName: '',
  streetAddress: '',
  locality: '',
  region: '',
  regionCode: '',
  postalCode: '',
  countryName: '',
  countryCode: '',
  password: '',
  confirmPassword: '',
  createAccount: false,
  savePayment: false,
  makeDefault: false,
  discountCode: '',
  hostApplication: {
    id: '',
    version: '',
    displayName: '',
  },
  platform: '',
  isNewPaymentMethod: true,
  newPaymentMethodToken: '',
  numberValidity: '',
  cvvValidity: '',
  expirationDateValidity: '',
  existingPaymentMethodToken: '',
  numberOfCopies: 1,
  selectedPlan: '',
  serial: '',
} as const;

function Purchase() {
  const role = useAppSelector(selectUserRole);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Stripe hooks
  const stripe = useStripe();
  const elements = useElements();

  const { id: productId } = useParams();
  const { data: productDetails, error: productError } =
    useGetProductDetailsQuery(productId ?? '');
  const { data: config, error: configError } = useGetConfigQuery();
  const [
    makePurchase,
    {
      data: confirmation,
      isSuccess: purchaseSuccess,
      error: purchaseError,
      isLoading: purchaseLoading,
    },
  ] = useMakePurchaseMutation();
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const [hostedFieldsInstance, setHostedFieldsInstance] =
    useState<braintree.HostedFields | null>(null);
  const user = useAppSelector(selectUser);

  // 🔁 Redirect logic - TEMPORARY FIX FOR CUSTOMERS GOING TO OLD FF2 LINK
  const restrictedProductId = '66341fef4895dadba053975d'; // FF2
  const newProductId = '67f6b5c241f8c9345e025d6d'; // FF3
  useEffect(() => {
    if (
      (role === 'user' || role === 'public') &&
      productId === restrictedProductId
    ) {
      navigate(`/purchase/${newProductId}`, { replace: true });
    }
  }, [role, productId, navigate]);

  // If the user is not logged in and making a public purchase for a subscription,
  // create an account and save the payment method
  useEffect(() => {
    if (productDetails?.category === 'Subscription' && !isLoggedIn) {
      // setFormData((prev) => ({
      //   ...prev,
      //   createAccount: true,
      //   savePayment: true,
      // }));
    }
  }, [productDetails, isLoggedIn]);

  if (productError) {
    return <FullScreenLoader message={formatQueryError(productError)} />;
  }
  if (configError) {
    return <FullScreenLoader message={formatQueryError(configError)} />;
  }
  if (!productDetails || !config) {
    return <FullScreenLoader message="Loading..." />;
  }

  const { braintreeClientToken } = config;

  if (purchaseSuccess && confirmation) {
    return <PurchaseConfirmationPage {...confirmation} />;
  }

  const handleSubmit = async (
    values: typeof initialForm,
    actions: { setFieldError: (arg0: string, arg1: string) => void },
  ) => {
    console.log('handleSubmit');
    if (isSubmitting) return;

    setIsSubmitting(true);

    let newPaymentMethodToken = '';
    const provider =
      productDetails?.category === 'Subscription' ? 'stripe' : 'braintree';
    if (productDetails?.category === 'Subscription') {
      if (!stripe || !elements) {
        setIsSubmitting(false);
        return;
      }

      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) {
        setIsSubmitting(false);
        return;
      }

      const payload = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });
      newPaymentMethodToken = payload.paymentMethod?.id || '';
    } else if (hostedFieldsInstance && values.isNewPaymentMethod) {
      try {
        const { nonce } = await hostedFieldsInstance.tokenize();
        newPaymentMethodToken = nonce;
      } catch (error) {
        // Handle the error here
        actions.setFieldError(
          'newPaymentMethodToken',
          'Encountered an error while tokenizing card details. Please try again.',
        );
      }
    }

    console.log('newPaymentMethodToken', newPaymentMethodToken);

    const {
      createAccount,
      firstName,
      lastName,
      email,
      password,
      confirmPassword,
      savePayment,
      existingPaymentMethodToken,
      isNewPaymentMethod,
      nameOnCard,
      streetAddress,
      locality,
      region,
      postalCode,
      countryName,
      discountCode,
      hostApplication,
      platform,
      numberOfCopies,
      makeDefault,
      serial,
    } = values;

    const request = {
      user: {
        id: '_id' in user ? user._id : undefined,
        create: createAccount,
        firstName,
        lastName,
        email,
        password,
        confirmPassword,
      },
      paymentMethod: {
        provider,
        id: isNewPaymentMethod ? undefined : existingPaymentMethodToken,
        create: isNewPaymentMethod ? savePayment : undefined,
        makeDefault: isNewPaymentMethod ? makeDefault : undefined,
        token: isNewPaymentMethod ? newPaymentMethodToken : undefined,
        billingAddress: isNewPaymentMethod
          ? {
              nameOnCard,
              streetAddress,
              locality,
              region,
              postalCode,
              countryName,
            }
          : undefined,
      },
      purchase: {
        productId: productDetails._id,
        priceId: productDetails.priceId,
        discountCode,
        serial,
        hostApplication,
        platform,
        numberOfCopies,
      },
    };
    console.log(request);
    console.log(JSON.stringify(request));
    makePurchase(request);
    setIsSubmitting(false);
  };
  return (
    <Formik
      initialValues={initialForm}
      validate={(values) => {
        let validationSchema: ZodSchema = PublicUserNoAccountSchema;
        if (values.createAccount) validationSchema = PublicUserNewAccountSchema;
        if (isLoggedIn) validationSchema = LoggedInUserNewCardSchema;
        if (!values.isNewPaymentMethod)
          validationSchema = LoggedInUserExistingCardSchema;
        return toValidator(validationSchema)(values);
      }}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue }) => {
        if (purchaseLoading) {
          return <FullScreenLoader message="Processing your purchase..." />;
        }

        return (
          <Form>
            <Box
              component="main"
              sx={{
                flexGrow: 1,
                pt: 0,
                pr: { xs: 2, sm: 5 },
                pb: { xs: 2, sm: 5 },
                pl: { xs: 2, sm: 5 },
              }}
            >
              <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                <MuiLink underline="hover" color="inherit" href="/">
                  Home
                </MuiLink>
                <Typography color="text.primary" fontWeight="bold">
                  Purchase
                </Typography>
              </Breadcrumbs>
              <Typography variant="h4" sx={{ mb: 3 }}>
                Purchase
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} lg={8} xl={8}>
                  <Stack spacing={2}>
                    {purchaseError && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {formatQueryError(purchaseError)}
                      </Alert>
                    )}

                    <MainCard>
                      {isLoggedIn && (
                        <FormControlLabel
                          control={
                            <Radio
                              checked={values.isNewPaymentMethod}
                              onChange={(e) =>
                                setFieldValue(
                                  'isNewPaymentMethod',
                                  e.target.checked,
                                )
                              }
                              value="newCard"
                            />
                          }
                          label="New Credit Card"
                          labelPlacement="end"
                        />
                      )}
                      <Box
                        sx={{
                          opacity: values.isNewPaymentMethod ? 1 : 0.4,
                          pointerEvents: values.isNewPaymentMethod
                            ? 'auto'
                            : 'none',
                        }}
                      >
                        {productDetails?.category === 'Subscription' ? (
                          <StripeCreditCardForm />
                        ) : (
                          <PaymentMethodForm
                            disabled={!values.isNewPaymentMethod}
                            clientToken={braintreeClientToken}
                            hostedFieldsInstance={hostedFieldsInstance}
                            setHostedFieldsInstance={setHostedFieldsInstance}
                          />
                        )}

                        <Typography variant="h6" sx={{ my: 2 }}>
                          Billing Address
                        </Typography>
                        <BillingAddressForm
                          disabled={!values.isNewPaymentMethod}
                        />

                        <AccountForm disabled={!values.isNewPaymentMethod} />
                      </Box>
                    </MainCard>

                    {isLoggedIn && (
                      <ExistingPaymentMethods
                        category={productDetails?.category}
                      />
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6} lg={4} xl={4}>
                  <Stack>
                    <MainCard
                      title="Order Summary"
                      sx={{ borderRadius: '4px 4px 0 0' }}
                      content={false}
                    >
                      <OrderSummary product={productDetails} />
                    </MainCard>
                    <MainCard sx={{ mt: 3 }}>
                      <Stack justifyContent="flex-end" spacing={1}>
                        <Typography
                          align="left"
                          variant="caption"
                          color="textSecondary"
                          sx={{ cursor: 'pointer' }}
                          onClick={() => {}}
                        >
                          Discount
                        </Typography>
                        <Stack justifyContent="flex-end">
                          <DiscountCode productId={productDetails._id} />
                        </Stack>
                      </Stack>
                    </MainCard>
                    <Button
                      variant="contained"
                      sx={{ my: 3 }}
                      type="submit"
                      fullWidth
                      disabled={isSubmitting}
                    >
                      Purchase
                    </Button>
                    <Link to="/">
                      <Button variant="outlined" color="primary" fullWidth>
                        Cancel
                      </Button>
                    </Link>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}

export default Purchase;
